import React, { useState, useEffect } from 'react';
import {
  Card,
  Select,
  Checkbox,
  Tabs,
  Table,
  Button,
  Space,
  Statistic,
  Row,
  Col,
  message,
  Spin,
} from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import {
  registryReportsApi,
  type ProgramType,
  type ConsentStatus,
  type CohortReport,
  type CasesReport,
  type IntervalsReport,
  type CompletenessReport,
} from '../../api/registryReportsApi';

const { Option } = Select;
const { TabPane } = Tabs;

const RegistryReportsPage: React.FC = () => {
  const [program, setProgram] = useState<ProgramType>('DERMATO');
  const [consentFilter, setConsentFilter] = useState<ConsentStatus[]>(['TICKED']);
  const [loading, setLoading] = useState(false);

  const [cohortData, setCohortData] = useState<CohortReport | null>(null);
  const [casesData, setCasesData] = useState<CasesReport | null>(null);
  const [intervalsData, setIntervalsData] = useState<IntervalsReport | null>(null);
  const [completenessData, setCompletenessData] = useState<CompletenessReport | null>(null);

  useEffect(() => {
    loadReports();
  }, [program, consentFilter]);

  const loadReports = async () => {
    setLoading(true);
    try {
      const filters = { consent: consentFilter };
      const [cohort, cases, intervals, completeness] = await Promise.all([
        registryReportsApi.getCohortReport(program, filters),
        registryReportsApi.getCasesReport(program, filters),
        registryReportsApi.getIntervalsReport(program, filters),
        registryReportsApi.getCompletenessReport(program, filters),
      ]);

      setCohortData(cohort);
      setCasesData(cases);
      setIntervalsData(intervals);
      setCompletenessData(completeness);
    } catch (error) {
      message.error('Eroare la încărcarea rapoartelor');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleConsentChange = (checked: boolean, value: ConsentStatus) => {
    if (checked) {
      setConsentFilter([...consentFilter, value]);
    } else {
      setConsentFilter(consentFilter.filter((v) => v !== value));
    }
  };

  const handleExportExcel = () => {
    registryReportsApi.downloadExcel(program, { consent: consentFilter });
  };

  const handleExportCSV = (sheet: string) => {
    registryReportsApi.downloadCSV(program, sheet, { consent: consentFilter });
  };

  const renderSimpleBarChart = (data: Record<string, number>, maxValue?: number) => {
    const entries = Object.entries(data);
    const max = maxValue || Math.max(...entries.map(([_, v]) => v));

    return (
      <div style={{ marginTop: 16 }}>
        {entries.map(([key, value]) => (
          <div key={key} style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 12, marginBottom: 2 }}>{key}: {value}</div>
            <div
              style={{
                width: `${(value / max) * 100}%`,
                height: 8,
                backgroundColor: '#1890ff',
                borderRadius: 4,
              }}
            />
          </div>
        ))}
      </div>
    );
  };

  const renderCohortTab = () => {
    if (!cohortData) return null;

    const demographicsColumns = [
      { title: 'Caracteristică', dataIndex: 'key', key: 'key' },
      { title: 'Valoare', dataIndex: 'value', key: 'value' },
    ];

    const demographicsData = [
      { key: 'Total pacienți', value: cohortData.totalPatients },
      { key: 'Vârstă medie', value: cohortData.ageStats.mean.toFixed(1) },
      { key: 'Vârstă mediană', value: cohortData.ageStats.median.toFixed(1) },
      { key: 'Vârstă min', value: cohortData.ageStats.min },
      { key: 'Vârstă max', value: cohortData.ageStats.max },
    ];

    return (
      <div>
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={8}>
            <Card>
              <Statistic
                title="Total pacienți"
                value={cohortData.totalPatients}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card>
              <Statistic
                title="Vârstă medie"
                value={cohortData.ageStats.mean.toFixed(1)}
                suffix="ani"
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card>
              <Statistic
                title="Vârstă mediană"
                value={cohortData.ageStats.median.toFixed(1)}
                suffix="ani"
              />
            </Card>
          </Col>
        </Row>

        <Table
          columns={demographicsColumns}
          dataSource={demographicsData}
          pagination={false}
          size="small"
          style={{ marginBottom: 24 }}
        />

        <Card title="Distribuție pe sex" style={{ marginBottom: 16 }}>
          {renderSimpleBarChart(cohortData.genderDistribution)}
        </Card>

        <Card title="Distribuție pe județ" style={{ marginBottom: 16 }}>
          {renderSimpleBarChart(cohortData.countyDistribution)}
        </Card>

        <Card title="Factori de risc" style={{ marginBottom: 16 }}>
          {renderSimpleBarChart(cohortData.riskFactors)}
        </Card>

        <Card title="Top 10 comorbidități">
          <Table
            columns={[
              { title: 'ICD-10', dataIndex: 'icd10', key: 'icd10' },
              { title: 'Număr cazuri', dataIndex: 'count', key: 'count' },
            ]}
            dataSource={cohortData.topComorbidities}
            pagination={false}
            size="small"
          />
        </Card>
      </div>
    );
  };

  const renderCasesTab = () => {
    if (!casesData) return null;

    return (
      <div>
        <Card title="Total cazuri" style={{ marginBottom: 16 }}>
          <Statistic value={casesData.totalCases} />
        </Card>

        <Card title="Pe categorie diagnostic" style={{ marginBottom: 16 }}>
          {renderSimpleBarChart(casesData.byCategory)}
        </Card>

        <Card title="Pe tip tumoral (ICD-10)" style={{ marginBottom: 16 }}>
          {renderSimpleBarChart(casesData.byTumorType)}
        </Card>

        <Card title="Pe bază diagnostic" style={{ marginBottom: 16 }}>
          {renderSimpleBarChart(casesData.byBasis)}
        </Card>

        <Card title="Pe an incidență" style={{ marginBottom: 16 }}>
          {renderSimpleBarChart(casesData.byIncidenceYear)}
        </Card>

        {casesData.byLocation && (
          <Card title="Pe localizare" style={{ marginBottom: 16 }}>
            {renderSimpleBarChart(casesData.byLocation)}
          </Card>
        )}

        {casesData.primaryVsMetastasis && (
          <Card title="Primar vs Metastază">
            {renderSimpleBarChart(casesData.primaryVsMetastasis)}
          </Card>
        )}
      </div>
    );
  };

  const renderIntervalsTab = () => {
    if (!intervalsData) return null;

    const columns = [
      { title: 'Interval', dataIndex: 'interval', key: 'interval' },
      { title: 'Mediană (zile)', dataIndex: 'median', key: 'median' },
      { title: 'Q1', dataIndex: 'q1', key: 'q1' },
      { title: 'Q3', dataIndex: 'q3', key: 'q3' },
    ];

    const data = [
      {
        key: '1',
        interval: 'Internare → Rezultat HP',
        median: intervalsData.admissionToHpResult.median.toFixed(1),
        q1: intervalsData.admissionToHpResult.iqr[0].toFixed(1),
        q3: intervalsData.admissionToHpResult.iqr[1].toFixed(1),
      },
      {
        key: '2',
        interval: 'Recoltare → Rezultat',
        median: intervalsData.collectionToResult.median.toFixed(1),
        q1: intervalsData.collectionToResult.iqr[0].toFixed(1),
        q3: intervalsData.collectionToResult.iqr[1].toFixed(1),
      },
      {
        key: '3',
        interval: 'Externare → Consult Onco',
        median: intervalsData.dischargeToOncoConsult.median.toFixed(1),
        q1: intervalsData.dischargeToOncoConsult.iqr[0].toFixed(1),
        q3: intervalsData.dischargeToOncoConsult.iqr[1].toFixed(1),
      },
      {
        key: '4',
        interval: 'Durată spitalizare',
        median: intervalsData.hospitalizationDuration.median.toFixed(1),
        q1: intervalsData.hospitalizationDuration.iqr[0].toFixed(1),
        q3: intervalsData.hospitalizationDuration.iqr[1].toFixed(1),
      },
      {
        key: '5',
        interval: 'Internări per pacient',
        median: intervalsData.admissionsPerPatient.median.toFixed(1),
        q1: intervalsData.admissionsPerPatient.iqr[0].toFixed(1),
        q3: intervalsData.admissionsPerPatient.iqr[1].toFixed(1),
      },
    ];

    return (
      <div>
        <Table columns={columns} dataSource={data} pagination={false} />
      </div>
    );
  };

  const renderCompletenessTab = () => {
    if (!completenessData) return null;

    const missingFieldsColumns = [
      { title: 'Câmp', dataIndex: 'field', key: 'field' },
      { title: 'Lipsă', dataIndex: 'count', key: 'count' },
    ];

    const missingFieldsData = Object.entries(
      completenessData.missingKeyFields,
    ).map(([field, count]) => ({
      key: field,
      field,
      count,
    }));

    return (
      <div>
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Card>
              <Statistic
                title="Documente/pacient (medie)"
                value={completenessData.documentsPerPatient.mean.toFixed(1)}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Alerte rezolvate"
                value={completenessData.alertsResolved}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Alerte nerezolvate"
                value={completenessData.alertsUnresolved}
                valueStyle={{ color: '#ff4d4f' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Pacienți în așteptare"
                value={completenessData.patientsPending}
                valueStyle={{ color: '#faad14' }}
              />
            </Card>
          </Col>
        </Row>

        <Card title="Câmpuri lipsă" style={{ marginBottom: 16 }}>
          <Table
            columns={missingFieldsColumns}
            dataSource={missingFieldsData}
            pagination={false}
            size="small"
          />
        </Card>

        <Card title="Distribuție consimțământ">
          {renderSimpleBarChart(completenessData.consentDistribution)}
        </Card>
      </div>
    );
  };

  return (
    <div style={{ padding: 24 }}>
      <h1>Rapoarte registru</h1>

      <Card style={{ marginBottom: 24 }}>
        <Space size="large">
          <div>
            <label style={{ marginRight: 8 }}>Program:</label>
            <Select
              value={program}
              onChange={setProgram}
              style={{ width: 150 }}
            >
              <Option value="DERMATO">Dermato</Option>
              <Option value="HEPATO">Hepato</Option>
            </Select>
          </div>

          <div>
            <label style={{ marginRight: 8 }}>Consimțământ:</label>
            <Space>
              <Checkbox
                checked={consentFilter.includes('TICKED')}
                onChange={(e) => handleConsentChange(e.target.checked, 'TICKED')}
              >
                Bifat
              </Checkbox>
              <Checkbox
                checked={consentFilter.includes('SIGNED_NO_TICKS')}
                onChange={(e) =>
                  handleConsentChange(e.target.checked, 'SIGNED_NO_TICKS')
                }
              >
                Semnat fără bife
              </Checkbox>
              <Checkbox
                checked={consentFilter.includes('MISSING')}
                onChange={(e) => handleConsentChange(e.target.checked, 'MISSING')}
              >
                Lipsă
              </Checkbox>
            </Space>
          </div>

          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={handleExportExcel}
          >
            Export XLSX
          </Button>
        </Space>
      </Card>

      <Spin spinning={loading}>
        <Tabs defaultActiveKey="cohort">
          <TabPane tab="Cohortă" key="cohort">
            {renderCohortTab()}
            <div style={{ marginTop: 16 }}>
              <Button onClick={() => handleExportCSV('patients')}>
                Export CSV Pacienți
              </Button>
            </div>
          </TabPane>

          <TabPane tab="Cazuri și diagnostic" key="cases">
            {renderCasesTab()}
            <div style={{ marginTop: 16 }}>
              <Button onClick={() => handleExportCSV('cases')}>
                Export CSV Cazuri
              </Button>
            </div>
          </TabPane>

          <TabPane tab="Intervale și parcurs" key="intervals">
            {renderIntervalsTab()}
            <div style={{ marginTop: 16 }}>
              <Button onClick={() => handleExportCSV('encounters')}>
                Export CSV Internări
              </Button>
            </div>
          </TabPane>

          <TabPane tab="Completitudine" key="completeness">
            {renderCompletenessTab()}
          </TabPane>
        </Tabs>
      </Spin>
    </div>
  );
};

export default RegistryReportsPage;
